import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface CadResponsavelItem {
  idCadResponsavel: number;
  responsavel: string;
  sigla: string;
  ordem: number;
}

const DRAGULA_BAG = 'cad-responsavel-ordem';

@Component({
  selector: 'app-cadastro-responsavel',
  templateUrl: './cadastro-responsavel.component.html',
  styleUrls: ['./cadastro-responsavel.component.scss'],
})
export class CadastroResponsavelComponent implements OnInit, OnDestroy {
  formResponsavel = '';
  formSigla = '';
  formOrdem = 0;
  filtroTexto = '';

  itens: CadResponsavelItem[] = [];
  itensFiltrados: CadResponsavelItem[] = [];
  itensPaginados: CadResponsavelItem[] = [];
  itemEdicao: CadResponsavelItem | null = null;
  modalRef: NgbModalRef | null = null;

  loading = false;
  salvando = false;
  reordenando = false;
  paginaAtual = 1;
  itensPorPagina = 10;

  private dragulaSub?: Subscription;

  constructor(
    private modalService: NgbModal,
    private ajaxService: AjaxService,
    private util: UtilService,
    private dragula: DragulaService,
  ) {}

  ngOnInit(): void {
    this.inicializarDragula();
    this.carregar();
  }

  ngOnDestroy(): void {
    this.dragulaSub?.unsubscribe();
    if (this.dragula.find(DRAGULA_BAG)) {
      this.dragula.destroy(DRAGULA_BAG);
    }
  }

  get podeReordenar(): boolean {
    return !this.filtroTexto.trim();
  }

  get totalPaginas(): number {
    return Math.ceil(this.itensFiltrados.length / this.itensPorPagina) || 1;
  }

  carregar(): void {
    this.loading = true;
    this.util.showLoading();
    this.ajaxService.get(`${API}empresa/CadResponsavel/Consultar`).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();
        if (response?.hasErrors || response?.HasErrors) {
          this.itens = [];
          this.aplicarFiltros();
          this.util.toasterShowError(
            'Erro',
            response?.message || response?.Message || 'Não foi possível carregar os responsáveis.',
          );
          return;
        }
        const lista = this.extrairListaResponse(response);
        this.itens = this.mapearLista(lista);
        this.aplicarFiltros();
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.itens = [];
        this.itensFiltrados = [];
        this.itensPaginados = [];
        this.util.toasterShowError('Erro', 'Não foi possível carregar os responsáveis.');
      },
    });
  }

  novo(): void {
    this.itemEdicao = null;
    this.formResponsavel = '';
    this.formSigla = '';
    const maxOrdem = this.itens.reduce((max, x) => Math.max(max, x.ordem ?? 0), 0);
    this.formOrdem = maxOrdem + 1;
  }

  abrirModalCadastro(content: TemplateRef<any>, item?: CadResponsavelItem): void {
    if (item) {
      this.editar(item);
    } else {
      this.novo();
    }
    this.modalRef = this.modalService.open(content, { size: 'lg' });
  }

  editar(item: CadResponsavelItem): void {
    this.itemEdicao = item;
    this.formResponsavel = item.responsavel;
    this.formSigla = item.sigla;
    this.formOrdem = item.ordem ?? 0;
  }

  salvar(): void {
    const responsavel = (this.formResponsavel || '').trim();
    const sigla = (this.formSigla || '').trim();
    if (!responsavel) {
      this.util.toasterShowWarning('Atenção', 'Informe o responsável.');
      return;
    }
    if (!sigla) {
      this.util.toasterShowWarning('Atenção', 'Informe a sigla.');
      return;
    }

    const idCad = Number(this.itemEdicao?.idCadResponsavel || 0);
    const ordem =
      idCad > 0
        ? Math.max(0, Math.trunc(Number(this.itemEdicao?.ordem ?? 0) || 0))
        : Math.max(0, Math.trunc(Number(this.formOrdem) || 0));
    const payload = {
      ID_CAD_RESPONSAVEL: idCad,
      RESPONSAVEL: responsavel,
      SIGLA: sigla,
      ORDEM: ordem,
    };

    this.salvando = true;
    this.ajaxService.post(`${API}empresa/CadResponsavel/SaveAsync`, payload).subscribe({
      next: (response) => {
        this.salvando = false;
        if (response?.hasErrors || response?.HasErrors) {
          this.util.toasterShowError(
            'Erro',
            response?.message || response?.Message || 'Não foi possível salvar o responsável.',
          );
          return;
        }
        this.util.toasterShowSuccess('Sucesso', idCad > 0 ? 'Responsável atualizado.' : 'Responsável cadastrado.');
        this.modalRef?.close();
        this.novo();
        this.carregar();
      },
      error: () => {
        this.salvando = false;
        this.util.toasterShowError('Erro', 'Falha ao salvar o responsável.');
      },
    });
  }

  excluir(item: CadResponsavelItem): void {
    const idCad = Number(item?.idCadResponsavel || 0);
    if (idCad <= 0) {
      return;
    }
    const rotulo = (item.responsavel || '').trim() || `#${idCad}`;
    if (!confirm(`Excluir o responsável "${rotulo}"?`)) {
      return;
    }

    this.util.showLoading();
    this.ajaxService.delete(`${API}empresa/CadResponsavel/DeleteAsync?id=${idCad}`).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors || response?.HasErrors) {
          this.util.toasterShowError(
            'Erro',
            response?.message || response?.Message || 'Não foi possível excluir o responsável.',
          );
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Responsável excluído.');
        if (this.itemEdicao?.idCadResponsavel === idCad) {
          this.novo();
        }
        this.carregar();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Falha ao excluir o responsável.');
      },
    });
  }

  aplicarFiltros(): void {
    const termo = (this.filtroTexto || '').toLowerCase().trim();
    this.itensFiltrados = this.itens.filter((x) => {
      if (!termo) {
        return true;
      }
      return (
        (x.responsavel || '').toLowerCase().includes(termo) ||
        (x.sigla || '').toLowerCase().includes(termo)
      );
    });
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  limparFiltros(): void {
    this.filtroTexto = '';
    this.aplicarFiltros();
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.atualizarPaginacao();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.atualizarPaginacao();
    }
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  private inicializarDragula(): void {
    if (!this.dragula.find(DRAGULA_BAG)) {
      this.dragula.createGroup(DRAGULA_BAG, {
        direction: 'vertical',
        moves: (_el, _container, handle) => {
          if (!handle) {
            return false;
          }
          return handle.classList.contains('drag-handle') || !!handle.closest('.drag-handle');
        },
      });
    }

    this.dragulaSub = this.dragula.drop(DRAGULA_BAG).subscribe(() => {
      if (!this.podeReordenar || this.reordenando) {
        return;
      }
      this.aplicarOrdemLocal();
      this.persistirOrdem();
    });
  }

  private aplicarOrdemLocal(): void {
    this.itens.forEach((item, index) => {
      item.ordem = index + 1;
    });
    this.aplicarFiltros();
  }

  private persistirOrdem(): void {
    const payload = this.itens.map((x) => ({
      ID_CAD_RESPONSAVEL: x.idCadResponsavel,
      ORDEM: x.ordem,
    }));

    this.reordenando = true;
    this.ajaxService.post(`${API}empresa/CadResponsavel/ReordenarAsync`, payload).subscribe({
      next: (response) => {
        this.reordenando = false;
        if (response?.hasErrors || response?.HasErrors) {
          this.util.toasterShowError(
            'Erro',
            response?.message || response?.Message || 'Não foi possível salvar a nova ordem.',
          );
          this.carregar();
        }
      },
      error: () => {
        this.reordenando = false;
        this.util.toasterShowError('Erro', 'Falha ao salvar a nova ordem.');
        this.carregar();
      },
    });
  }

  private atualizarPaginacao(): void {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.itensPaginados = this.itensFiltrados.slice(inicio, fim);
  }

  private extrairListaResponse(response: any): unknown[] {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (Array.isArray(atual)) {
        break;
      }
      if (
        atual &&
        typeof atual === 'object' &&
        Object.prototype.hasOwnProperty.call(atual, 'data') &&
        atual.data != null
      ) {
        atual = atual.data;
      } else if (
        atual &&
        typeof atual === 'object' &&
        Object.prototype.hasOwnProperty.call(atual, 'Data') &&
        atual.Data != null
      ) {
        atual = atual.Data;
      } else {
        break;
      }
    }
    return this.normalizarLista(atual);
  }

  private normalizarLista(valor: any): unknown[] {
    if (Array.isArray(valor)) {
      return valor;
    }
    if (valor && typeof valor === 'object') {
      const valores = Object.values(valor).filter((x) => x && typeof x === 'object');
      if (valores.length > 0) {
        return valores;
      }
    }
    return [];
  }

  private mapearLista(lista: unknown[]): CadResponsavelItem[] {
    return (lista || [])
      .map((raw) => {
        const item = this.util.converterParaLowercase(raw || {}) as Record<string, unknown>;
        const idCad = Number(
          item.id ??
            item.id_cad_responsavel ??
            item.idcadresponsavel ??
            item.id_CAD_RESPONSAVEL ??
            0,
        );
        const responsavel = String(item.responsavel ?? item.RESPONSAVEL ?? '').trim();
        const sigla = String(item.sigla ?? item.SIGLA ?? '').trim();
        const ordem = Math.max(0, Math.trunc(Number(item.ordem ?? item.ORDEM ?? 0) || 0));
        return { idCadResponsavel: idCad, responsavel, sigla, ordem };
      })
      .filter((x) => x.idCadResponsavel > 0)
      .sort((a, b) => a.ordem - b.ordem || a.responsavel.localeCompare(b.responsavel) || a.sigla.localeCompare(b.sigla));
  }
}
